import { useTranslation } from "@redotech/redo-i18n";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import type { ReturnMethodState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";

function exchangeMatchOptions(
  t: (key: string) => string,
): RadioGroupOption<boolean>[] {
  return [
    { id: false, label: t("Customer chooses exchange") },
    { id: true, label: t("Has any exchange item") },
  ];
}

function returnMethodLabel(
  method: ReturnOptionMethod["type"],
  t: (key: string) => string,
): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (method) {
    case ReturnOptionMethod.EXCHANGE:
      return t("Exchange");
    case ReturnOptionMethod.REFUND:
      return t("Refund");
    case ReturnOptionMethod.STORE_CREDIT:
      return t("Store credit");
  }
  return t("Unknown");
}

export const ReturnMethodDetails = memo(function ReturnMethodDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<ReturnMethodState>) {
  const { t } = useTranslation();
  const returnMethodOptions: RedoListItem<ReturnOptionMethod["type"]>[] =
    useMemo(
      () =>
        (
          [
            ReturnOptionMethod.EXCHANGE,
            ReturnOptionMethod.REFUND,
            ReturnOptionMethod.STORE_CREDIT,
          ] as const
        ).map((method) => ({
          id: method,
          value: method,
          type: "text" as const,
          text: returnMethodLabel(method, t),
        })),
      [t],
    );

  const selectedMethod = state.returnMethod
    ? returnMethodOptions.find((option) => option.value === state.returnMethod)
    : undefined;

  return (
    <>
      <RedoSingleSelectDropdownInput
        description={
          flowType === FlowType.FINALIZE_CLAIM
            ? t(
                "Condition will match if any selected items have this compensation method",
              )
            : undefined
        }
        disabled={disabled}
        flexGrow
        label={
          flowType === FlowType.FINALIZE_CLAIM
            ? t("Compensation method")
            : t("Return method")
        }
        options={returnMethodOptions}
        optionSelected={({ item }) =>
          setState({ ...state, returnMethod: item.value })
        }
        placeholder={t("Select method...")}
        selectedItem={selectedMethod}
        size="xs"
      />
      {state.returnMethod === ReturnOptionMethod.EXCHANGE && (
        <RedoRadioGroup
          disabled={disabled}
          label={t("Exchange matches when")}
          options={exchangeMatchOptions(t)}
          setValue={(hasAnyExchangeItem) =>
            setState({ ...state, hasAnyExchangeItem })
          }
          size="xs"
          value={state.hasAnyExchangeItem}
        />
      )}
    </>
  );
});
